// src/app/[lang]/components/NavigationMenu.tsx
// Main navigation component with mobile drawer and desktop menu
// Updated: Testing deployment with deploy.sh not tracked
'use client';
import React, { useState, useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import {
  AppBar,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Box,
  Drawer,
  List,
  ListItemText,
  ListItemButton,
  ButtonGroup,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../theme/theme';
import {
  Bars3Icon as MenuIcon,
  ChevronDownIcon as KeyboardArrowDownIcon,
  ChevronUpIcon as ExpandLess,
  ChevronDownIcon as ExpandMore,
  EnvelopeIcon
} from '@heroicons/react/24/solid';
import Link from 'next/link';
import Collapse from '@mui/material/Collapse';
import LanguageSwitcher from './LanguageSwitcher';
import ModalSearch from './ModalSearch';
import { GetJournalIssuesForNavigationQuery } from '@/gql/gql-generated';

// Social media icon components using simple SVG paths
const FacebookIcon = ({ fontSize }: { fontSize?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: fontSize === 'inherit' ? '1em' : '18px', height: fontSize === 'inherit' ? '1em' : '18px' }}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const YouTubeIcon = ({ fontSize }: { fontSize?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: fontSize === 'inherit' ? '1em' : '18px', height: fontSize === 'inherit' ? '1em' : '18px' }}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const TwitterIcon = ({ fontSize }: { fontSize?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: fontSize === 'inherit' ? '1em' : '18px', height: fontSize === 'inherit' ? '1em' : '18px' }}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const EmailIcon = ({ fontSize }: { fontSize?: string }) => (
  <EnvelopeIcon style={{ width: fontSize === 'inherit' ? '1em' : '18px', height: fontSize === 'inherit' ? '1em' : '18px' }} />
);

// Constants
const SOCIAL_LINKS = [
  { Icon: FacebookIcon, href: 'https://www.facebook.com/Bolsheviks' },
  { Icon: YouTubeIcon, href: 'https://www.youtube.com/user/ibt1917' },
  { Icon: TwitterIcon, href: 'https://www.twitter.com/IBT1917' },
  { Icon: EmailIcon, href: 'mailto:ibt@bolshevik.org' },
] as const;

// Every visible label, so the French site is not navigated in English (WCAG 3.1.2)
const MENU_TRANSLATIONS = {
  HOME: { EN: 'home', FR: 'accueil' },
  JOURNAL: {
    EN: '1917 journal',
    FR: '1917 édition française',
  },
  JOURNAL_HOME: {
    EN: 'journal home',
    FR: 'accueil du journal',
  },
  BOOKS: { EN: 'books', FR: 'livres' },
  ARCHIVE: { EN: 'marxist archive', FR: 'archives marxistes' },
  ABOUT: { EN: 'about', FR: 'à propos' },
  DONATE: { EN: 'donate', FR: 'faire un don' },
  OPEN_MENU: { EN: 'Open menu', FR: 'Ouvrir le menu' },
  SITE_NAV: { EN: 'Site navigation', FR: 'Navigation du site' },
} as const;

interface NavigationMenuProps {
  lang: string;
  journalIssues: GetJournalIssuesForNavigationQuery;
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  lang,
  journalIssues,
}) => {
  const router = useRouter();

  // State management
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElJournal, setAnchorElJournal] = useState<null | HTMLElement>(null);
  const [journalOpen, setJournalOpen] = useState(false);

  // Sort journal issues with stable memoization
  const sortedJournalIssues = useMemo(() => {
    if (!journalIssues?.journalIssues?.nodes) {
      return [];
    }

    const sorted = [...journalIssues.journalIssues.nodes].sort((a, b) => {
      const extractNumber = (title: string | null | undefined) => {
        if (!title) return 0;
        const match = title.match(/No\.\s*(\d+)/i);
        return match ? parseInt(match[1], 10) : 0;
      };
      return extractNumber(b.title) - extractNumber(a.title);
    });

    return sorted;
  }, [journalIssues?.journalIssues?.nodes]);

  // Helper function to get translation
  const getTranslation = useCallback(
    (key: keyof typeof MENU_TRANSLATIONS) => {
      return (
        MENU_TRANSLATIONS[key][lang.toUpperCase() as 'EN' | 'FR'] ||
        MENU_TRANSLATIONS[key]['EN']
      );
    },
    [lang]
  );

  // Smart prefetch function - only prefetch on hover intent
  const handlePrefetch = useCallback((href: string) => {
    router.prefetch(href);
  }, [router]);

  // Memoized handlers
  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElJournal(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorElJournal(null);
  }, []);

  const handleNestedListToggle = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setJournalOpen((prev) => !prev);
  }, []);

  // Memoized drawer content
  const drawer = useMemo(
    () => (
      <Box
        sx={{
          textAlign: 'center',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <List sx={{ flexGrow: 1 }}>
          <ListItemButton
            sx={{
              p: 0,
              '& .MuiSelect-select': {
                width: '100%',
                py: 2,
                pl: 2,
              },
            }}
          >
            <LanguageSwitcher
              currentLang={lang}
              isMobile={true}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  width: '100%',
                },
              }}
            />
          </ListItemButton>

          <ListItemButton
            component={Link}
            href={`/${lang}`}
            onClick={handleDrawerToggle}
            prefetch={true}
          >
            <ListItemText primary={getTranslation('HOME')} />
          </ListItemButton>

          <ListItemButton onClick={handleNestedListToggle}>
            <ListItemText primary={getTranslation('JOURNAL')} />
            {journalOpen ? <ExpandLess className="w-4 h-4" /> : <ExpandMore className="w-4 h-4" />}
          </ListItemButton>
          
          <Collapse in={journalOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton
                component={Link}
                href={`/${lang}/journal`}
                sx={{ pl: 4 }}
                onClick={handleDrawerToggle}
                prefetch={true}
                onMouseEnter={() => handlePrefetch(`/${lang}/journal`)}
              >
                <ListItemText primary={getTranslation('JOURNAL_HOME')} />
              </ListItemButton>

              {sortedJournalIssues.length === 0 ? (
                <ListItemButton disabled sx={{ pl: 6 }}>
                  <ListItemText primary="Loading..." />
                </ListItemButton>
              ) : (
                sortedJournalIssues.map((issue, index) => (
                  <ListItemButton
                    key={issue.slug}
                    component={Link}
                    href={`/${lang}/journal/${issue.slug}`}
                    sx={{ pl: 6 }}
                    onClick={handleDrawerToggle}
                    prefetch={index < 3}
                    onMouseEnter={() => {
                      if (index >= 3) {
                        handlePrefetch(`/${lang}/journal/${issue.slug}`);
                      }
                    }}
                  >
                    <ListItemText primary={issue.title?.toLowerCase()} />
                  </ListItemButton>
                ))
              )}
            </List>
          </Collapse>

          <ListItemButton
            component={Link}
            href={`/${lang}/book`}
            onClick={handleDrawerToggle}
            prefetch={true}
            onMouseEnter={() => handlePrefetch(`/${lang}/book`)}
          >
            <ListItemText primary={getTranslation('BOOKS')} />
          </ListItemButton>

          <ListItemButton
            component={Link}
            href={`/${lang}/page/marxist-archive`}
            onClick={handleDrawerToggle}
            prefetch={false}
            onMouseEnter={() => handlePrefetch(`/${lang}/page/marxist-archive`)}
          >
            <ListItemText primary={getTranslation('ARCHIVE')} />
          </ListItemButton>

          <ListItemButton
            component={Link}
            href={`/${lang}/page/about`}
            onClick={handleDrawerToggle}
            prefetch={false}
            onMouseEnter={() => handlePrefetch(`/${lang}/page/about`)}
          >
            <ListItemText primary={getTranslation('ABOUT')} />
          </ListItemButton>

          <ListItemButton
            component={Link}
            href={`/${lang}/page/donate`}
            onClick={handleDrawerToggle}
            prefetch={false}
            onMouseEnter={() => handlePrefetch(`/${lang}/page/donate`)}
          >
            <ListItemText primary={getTranslation('DONATE')} />
          </ListItemButton>
        </List>

        {/* Social icons at the bottom of drawer */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            '& .MuiIconButton-root': {
              fontSize: '1.45rem',
            },
          }}
        >
          {SOCIAL_LINKS.map(({ Icon, href }, index) => (
            <IconButton
              key={index}
              color="inherit"
              component={Link}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDrawerToggle}
              prefetch={false}
              aria-label={
                href.includes('facebook')
                  ? 'Visit our Facebook page'
                  : href.includes('youtube')
                    ? 'Visit our YouTube channel'
                    : href.includes('twitter')
                      ? 'Follow us on Twitter'
                      : href.includes('mailto')
                        ? 'Send us an email'
                        : 'Social media link'
              }
            >
              <Icon fontSize="inherit" />
            </IconButton>
          ))}
        </Box>
      </Box>
    ),
    [
      lang,
      journalOpen,
      sortedJournalIssues,
      handleDrawerToggle,
      handleNestedListToggle,
      getTranslation,
      handlePrefetch,
    ]
  );

  // Memoized social icons
  const socialIcons = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          '& .MuiIconButton-root': {
            fontSize: '1.45rem',
          },
        }}
      >
        {SOCIAL_LINKS.map(({ Icon, href }, index) => (
          <IconButton
            key={index}
            color="inherit"
            component={Link}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            prefetch={false}
            aria-label={
              href.includes('facebook')
                ? 'Visit our Facebook page'
                : href.includes('youtube')
                  ? 'Visit our YouTube channel'
                  : href.includes('twitter')
                    ? 'Follow us on Twitter'
                    : href.includes('mailto')
                      ? 'Send us an email'
                      : 'Social media link'
            }
          >
            <Icon fontSize="inherit" />
          </IconButton>
        ))}
      </Box>
    ),
    []
  );

  return (
    <ThemeProvider theme={theme}>
      <AppBar position="static" color="default" component="nav" aria-label="Main navigation">
        <Toolbar
          sx={{ justifyContent: 'space-between', flexWrap: 'wrap' }}
          className="-ml-2 md:mt-4"
          disableGutters
          variant="dense"
        >
          {/* Mobile hamburger menu */}
          <IconButton
            color="inherit"
            aria-label={getTranslation('OPEN_MENU')}
            aria-expanded={mobileOpen}
            aria-controls={mobileOpen ? 'mobile-nav-drawer' : undefined}
            size="large"
            className="p-1"
            onClick={handleDrawerToggle}
            sx={{
              display: { xs: 'flex', md: 'none' },
              fontSize: 'large',
            }}
          >
            <MenuIcon className="w-8 h-8" />
          </IconButton>

          {/* Desktop navigation */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexGrow: 1,
              alignItems: 'center',
            }}
          >
            <ButtonGroup
              variant="text"
              aria-label="text button group"
              sx={{
                '& .MuiButton-root': {
                  textTransform: 'none',
                },
              }}
            >
              <Button 
                component={Link} 
                href={`/${lang}`}
                prefetch={true}
                onMouseEnter={() => handlePrefetch(`/${lang}`)}
              >
                {getTranslation('HOME')}
              </Button>
              
              {/* MUI's documented menu-button pattern: the state lives on the trigger */}
              <Button
                id="journal-button"
                onClick={handleMenuOpen}
                endIcon={<KeyboardArrowDownIcon className="w-5 h-5" />}
                onMouseEnter={() => handlePrefetch(`/${lang}/journal`)}
                aria-haspopup="menu"
                aria-expanded={Boolean(anchorElJournal)}
                aria-controls={anchorElJournal ? 'journal-menu' : undefined}
              >
                {getTranslation('JOURNAL')}
              </Button>

              <Button 
                component={Link} 
                href={`/${lang}/book`}
                prefetch={true}
                onMouseEnter={() => handlePrefetch(`/${lang}/book`)}
              >
                {getTranslation('BOOKS')}
              </Button>
              
              <Button 
                component={Link} 
                href={`/${lang}/page/marxist-archive`}
                prefetch={false}
                onMouseEnter={() => handlePrefetch(`/${lang}/page/marxist-archive`)}
              >
                {getTranslation('ARCHIVE')}
              </Button>
              
              <Button 
                component={Link} 
                href={`/${lang}/page/about`}
                prefetch={false}
                onMouseEnter={() => handlePrefetch(`/${lang}/page/about`)}
              >
                {getTranslation('ABOUT')}
              </Button>
              
              <Button 
                component={Link} 
                href={`/${lang}/page/donate`}
                prefetch={false}
                onMouseEnter={() => handlePrefetch(`/${lang}/page/donate`)}
              >
                {getTranslation('DONATE')}
              </Button>
            </ButtonGroup>
          </Box>

          {/* Mobile search */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'transparent',
                '& .MuiButton-root': {
                  color: '#000000',
                  fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  lineHeight: 1.75,
                  textTransform: 'none',
                  opacity: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '& .MuiButton-startIcon': {
                    color: '#000000',
                  },
                },
              }}
            >
              <ModalSearch />
            </Box>
          </Box>

          {/* Desktop social icons */}
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>{socialIcons}</Box>
        </Toolbar>

        {/* Journal dropdown menu for desktop */}
        <Menu
          id="journal-menu"
          anchorEl={anchorElJournal}
          open={Boolean(anchorElJournal)}
          onClose={handleMenuClose}
          disableScrollLock={true}
          MenuListProps={{
            'aria-labelledby': 'journal-button',
          }}
          // 22 items at ~41px is taller than a 900px window; without a cap MUI
          // shifts the whole menu up over the logo and its own trigger.
          slotProps={{ paper: { sx: { maxHeight: '70vh' } } }}
        >
          <MenuItem
            onClick={handleMenuClose}
            component={Link}
            href={`/${lang}/journal`}
            prefetch={true}
            onMouseEnter={() => handlePrefetch(`/${lang}/journal`)}
          >
            {getTranslation('JOURNAL_HOME')}
          </MenuItem>

          {sortedJournalIssues.length === 0 ? (
            <MenuItem disabled>Loading...</MenuItem>
          ) : (
            sortedJournalIssues.map((issue, index) => (
              <MenuItem
                key={issue.slug}
                onClick={handleMenuClose}
                component={Link}
                href={`/${lang}/journal/${issue.slug}`}
                prefetch={index < 3}
                onMouseEnter={() => {
                  if (index >= 3) {
                    handlePrefetch(`/${lang}/journal/${issue.slug}`);
                  }
                }}
              >
                {issue.title?.toLowerCase()}
              </MenuItem>
            ))
          )}
        </Menu>

        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          slotProps={{
            paper: { id: 'mobile-nav-drawer', 'aria-label': getTranslation('SITE_NAV') },
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 240,
              '& svg': {
                width: '1rem',
                height: '1rem',
              }
            },
          }}
        >
          {drawer}
        </Drawer>
      </AppBar>
    </ThemeProvider>
  );
};

export default React.memo(NavigationMenu);